<script lang="ts">
  import { ArrowDown, ArrowUp, CalendarDays } from "lucide-svelte";
  import { Button } from "$lib/components/ui/button/index.js";
  import { Badge } from "$lib/components/ui/badge/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import type { CvrSortBy } from "$lib/types/cvr-sort-by";
  import type { CvrSortDirection } from "$lib/types/cvr-sort-direction";

  type SortByOption = {
    value: CvrSortBy;
    label: string;
  };

  type TableToolbarProps = {
    sortByOptions?: readonly SortByOption[];
    sortBy?: CvrSortBy;
    sortDirection?: CvrSortDirection;
    isLoading?: boolean;
    searchInput?: string;
    fromRow?: number;
    toRow?: number;
    totalCount?: number;
    onSortByChange?: (value: number) => void;
    onSortDirectionToggle?: () => void;
    onSearchInput?: (value: string) => void;
    minEmployees?: number | null;
    maxEmployees?: number | null;
    createdFrom?: string | null;
    createdTo?: string | null;
    companyForms?: string[];
    selectedCompanyForms?: string[];
    reklamebeskyttetFilter?: boolean | null;
    hasWebsiteFilter?: boolean | null;
    activeFilterBadges?: string[];
    onMinEmployeesChange?: (value: number | null) => void;
    onMaxEmployeesChange?: (value: number | null) => void;
    onCreatedFromChange?: (value: string | null) => void;
    onCreatedToChange?: (value: string | null) => void;
    onCompanyFormToggle?: (value: string) => void;
    onReklamebeskyttetFilterChange?: (value: boolean | null) => void;
    onHasWebsiteFilterChange?: (value: boolean | null) => void;
    onFilterBadgeClear?: (badge: string) => void;
  };

  let {
    sortByOptions = [],
    sortBy = 0,
    sortDirection = 0,
    isLoading = false,
    searchInput = "",
    fromRow = 0,
    toRow = 0,
    totalCount = 0,
    onSortByChange = (_value: number) => {},
    onSortDirectionToggle = () => {},
    onSearchInput = (_value: string) => {},
    minEmployees = null,
    maxEmployees = null,
    createdFrom = null,
    createdTo = null,
    companyForms = [],
    selectedCompanyForms = [],
    reklamebeskyttetFilter = null,
    hasWebsiteFilter = null,
    activeFilterBadges = [],
    onMinEmployeesChange = (_value: number | null) => {},
    onMaxEmployeesChange = (_value: number | null) => {},
    onCreatedFromChange = (_value: string | null) => {},
    onCreatedToChange = (_value: string | null) => {},
    onCompanyFormToggle = (_value: string) => {},
    onReklamebeskyttetFilterChange = (_value: boolean | null) => {},
    onHasWebsiteFilterChange = (_value: boolean | null) => {},
    onFilterBadgeClear = (_badge: string) => {}
  }: TableToolbarProps = $props();
  let showFilters = $state(false);
  let createdFromDraft = $state("");
  let createdToDraft = $state("");
  let isEditingCreatedFrom = $state(false);
  let isEditingCreatedTo = $state(false);
  let createdFromPickerRef = $state<HTMLInputElement | null>(null);
  let createdToPickerRef = $state<HTMLInputElement | null>(null);

  const handleSortByChange = (event: Event) => {
    const target = event.currentTarget as HTMLSelectElement;
    onSortByChange(Number(target.value));
  };

  const handleSearchInput = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement;
    onSearchInput(target.value);
  };

  const handleSortDirectionClick = () => {
    onSortDirectionToggle();
  };

  const handleMinEmployeesInput = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement;
    const value = target.value.trim();
    onMinEmployeesChange(value.length === 0 ? null : Number(value));
  };

  const handleMaxEmployeesInput = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement;
    const value = target.value.trim();
    onMaxEmployeesChange(value.length === 0 ? null : Number(value));
  };

  const handleCreatedFromInput = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement;
    createdFromDraft = target.value;
    if (createdFromDraft.trim().length === 0) {
      onCreatedFromChange(null);
    }
  };

  const handleCreatedToInput = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement;
    createdToDraft = target.value;
    if (createdToDraft.trim().length === 0) {
      onCreatedToChange(null);
    }
  };

  const formatIsoToDmy = (value: string | null) => {
    if (!value) {
      return "";
    }

    const [year, month, day] = value.split("-");
    if (!year || !month || !day) {
      return "";
    }

    return `${day}-${month}-${year}`;
  };

  const parseDmyToIso = (value: string): string | null => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return null;
    }

    let normalizedInput = trimmed;
    if (/^\d{8}$/.test(normalizedInput)) {
      normalizedInput = `${normalizedInput.slice(0, 2)}-${normalizedInput.slice(2, 4)}-${normalizedInput.slice(4, 8)}`;
    } else if (/^\d{6}$/.test(normalizedInput)) {
      const dayPart = normalizedInput.slice(0, 2);
      const monthPart = normalizedInput.slice(2, 4);
      const yearPart = Number(normalizedInput.slice(4, 6));
      const fullYear = yearPart >= 50 ? 1900 + yearPart : 2000 + yearPart;
      normalizedInput = `${dayPart}-${monthPart}-${fullYear}`;
    } else {
      normalizedInput = normalizedInput.replace(/[./\s]+/g, "-");
    }

    const match = /^(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})$/.exec(normalizedInput);
    if (!match) {
      return null;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const rawYear = Number(match[3]);
    const year = match[3].length === 2
      ? (rawYear >= 50 ? 1900 + rawYear : 2000 + rawYear)
      : rawYear;
    if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
      return null;
    }

    const candidate = new Date(Date.UTC(year, month - 1, day));
    if (
      candidate.getUTCFullYear() !== year ||
      candidate.getUTCMonth() !== month - 1 ||
      candidate.getUTCDate() !== day
    ) {
      return null;
    }

    return `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  };

  const commitCreatedFrom = () => {
    isEditingCreatedFrom = false;
    const iso = parseDmyToIso(createdFromDraft);
    if (createdFromDraft.trim().length > 0 && iso === null) {
      createdFromDraft = formatIsoToDmy(createdFrom);
      return;
    }

    onCreatedFromChange(iso);
    createdFromDraft = formatIsoToDmy(iso);
  };

  const commitCreatedTo = () => {
    isEditingCreatedTo = false;
    const iso = parseDmyToIso(createdToDraft);
    if (createdToDraft.trim().length > 0 && iso === null) {
      createdToDraft = formatIsoToDmy(createdTo);
      return;
    }

    onCreatedToChange(iso);
    createdToDraft = formatIsoToDmy(iso);
  };

  const handleFromPickerChange = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement;
    const iso = target.value.trim().length === 0 ? null : target.value;
    onCreatedFromChange(iso);
    createdFromDraft = formatIsoToDmy(iso);
  };

  const handleToPickerChange = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement;
    const iso = target.value.trim().length === 0 ? null : target.value;
    onCreatedToChange(iso);
    createdToDraft = formatIsoToDmy(iso);
  };

  const openPicker = (picker: HTMLInputElement | null) => {
    if (!picker) {
      return;
    }

    const maybePicker = picker as HTMLInputElement & { showPicker?: () => void };
    if (typeof maybePicker.showPicker === "function") {
      maybePicker.showPicker();
      return;
    }

    picker.click();
  };

  $effect(() => {
    if (isEditingCreatedFrom) {
      return;
    }

    const formatted = formatIsoToDmy(createdFrom);
    if (createdFromDraft !== formatted) {
      createdFromDraft = formatted;
    }
  });

  $effect(() => {
    if (isEditingCreatedTo) {
      return;
    }

    const formatted = formatIsoToDmy(createdTo);
    if (createdToDraft !== formatted) {
      createdToDraft = formatted;
    }
  });

  const handleReklamebeskyttetChange = (event: Event) => {
    const target = event.currentTarget as HTMLSelectElement;
    if (target.value === "all") {
      onReklamebeskyttetFilterChange(null);
      return;
    }

    onReklamebeskyttetFilterChange(target.value === "true");
  };

  const handleHasWebsiteChange = (event: Event) => {
    const target = event.currentTarget as HTMLSelectElement;
    if (target.value === "all") {
      onHasWebsiteFilterChange(null);
      return;
    }

    onHasWebsiteFilterChange(target.value === "true");
  };
</script>

<div class="flex flex-wrap items-center justify-between gap-3">
  <div class="flex items-center gap-2">
    <label for="table-sort-by" class="text-sm text-muted-foreground">Sort by</label>
    <select
      id="table-sort-by"
      class="h-9 rounded-md border border-input bg-background px-3 text-sm"
      onchange={handleSortByChange}
    >
      {#each sortByOptions as option}
        <option value={option.value} selected={option.value === sortBy}>{option.label}</option>
      {/each}
    </select>
    <Button
      type="button"
      variant="outline"
      size="icon"
      onclick={handleSortDirectionClick}
      disabled={isLoading}
      aria-label={sortDirection === 0 ? "Sort ascending" : "Sort descending"}
    >
      {#if sortDirection === 0}
        <ArrowUp class="h-4 w-4" />
      {:else}
        <ArrowDown class="h-4 w-4" />
      {/if}
    </Button>
  </div>

  <div class="flex w-full max-w-md items-center gap-2">
    <Input
      placeholder="Search name, branche, address or CVR..."
      value={searchInput}
      oninput={handleSearchInput}
    />
  </div>

  <div class="text-sm text-muted-foreground">
    Showing {fromRow}-{toRow} of {totalCount}
  </div>
</div>

<div class="flex items-center justify-between">
  <Button
    type="button"
    variant="outline"
    size="sm"
    onclick={() => { showFilters = !showFilters; }}
  >
    {showFilters ? "Hide filters" : "Show filters"}
  </Button>
</div>

{#if showFilters}
<div class="grid gap-3 rounded-md border border-border p-3 md:grid-cols-12">
  <div class="grid gap-2 md:col-span-6">
    <p class="text-sm text-muted-foreground">Employees (range)</p>
    <div class="flex items-center gap-2">
      <Input
        type="number"
        min="0"
        placeholder="Min"
        value={minEmployees ?? ""}
        oninput={handleMinEmployeesInput}
      />
      <span class="text-muted-foreground">to</span>
      <Input
        type="number"
        min="0"
        placeholder="Max"
        value={maxEmployees ?? ""}
        oninput={handleMaxEmployeesInput}
      />
    </div>
  </div>

  <div class="grid gap-2 md:col-span-3">
    <p class="text-xs text-muted-foreground">Marketing opt-out</p>
    <select
      class="h-9 rounded-md border border-input bg-background px-3 text-sm"
      onchange={handleReklamebeskyttetChange}
    >
      <option value="all" selected={reklamebeskyttetFilter === null}>All</option>
      <option value="true" selected={reklamebeskyttetFilter === true}>Yes</option>
      <option value="false" selected={reklamebeskyttetFilter === false}>No</option>
    </select>
  </div>

  <div class="grid gap-2 md:col-span-3">
    <p class="text-xs text-muted-foreground">Website</p>
    <select
      class="h-9 rounded-md border border-input bg-background px-3 text-sm"
      onchange={handleHasWebsiteChange}
    >
      <option value="all" selected={hasWebsiteFilter === null}>All</option>
      <option value="true" selected={hasWebsiteFilter === true}>Yes</option>
      <option value="false" selected={hasWebsiteFilter === false}>No</option>
    </select>
  </div>

  <div class="md:col-span-12 grid gap-2">
    <p class="text-sm text-muted-foreground">Created (range)</p>
    <div class="flex items-center gap-2 md:max-w-xl">
      <div class="relative w-full">
        <Input
          type="text"
          placeholder="dd-mm-yyyy"
          value={createdFromDraft}
          onfocus={() => { isEditingCreatedFrom = true; }}
          oninput={handleCreatedFromInput}
          onblur={commitCreatedFrom}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          class="absolute top-1/2 right-1 -translate-y-1/2 cursor-pointer"
          onclick={() => openPicker(createdFromPickerRef)}
          aria-label="Pick created from date"
        >
          <CalendarDays class="h-4 w-4" />
        </Button>
        <input
          bind:this={createdFromPickerRef}
          type="date"
          class="sr-only"
          value={createdFrom ?? ""}
          onchange={handleFromPickerChange}
        />
      </div>
      <span class="text-muted-foreground">to</span>
      <div class="relative w-full">
        <Input
          type="text"
          placeholder="dd-mm-yyyy"
          value={createdToDraft}
          onfocus={() => { isEditingCreatedTo = true; }}
          oninput={handleCreatedToInput}
          onblur={commitCreatedTo}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          class="absolute top-1/2 right-1 -translate-y-1/2 cursor-pointer"
          onclick={() => openPicker(createdToPickerRef)}
          aria-label="Pick created to date"
        >
          <CalendarDays class="h-4 w-4" />
        </Button>
        <input
          bind:this={createdToPickerRef}
          type="date"
          class="sr-only"
          value={createdTo ?? ""}
          onchange={handleToPickerChange}
        />
      </div>
    </div>
  </div>

  <div class="md:col-span-12 grid gap-2">
    <p class="text-sm text-muted-foreground">Company forms</p>
    <div class="flex flex-wrap gap-2">
      {#each companyForms as companyForm}
        <button
          type="button"
          class={selectedCompanyForms.includes(companyForm)
            ? "inline-flex h-7 items-center rounded-md bg-primary px-2 text-xs font-medium text-primary-foreground cursor-pointer"
            : "inline-flex h-7 items-center rounded-md border border-input px-2 text-xs font-medium cursor-pointer hover:bg-muted"}
          onclick={() => onCompanyFormToggle(companyForm)}
        >
          {companyForm}
        </button>
      {/each}
      {#if companyForms.length === 0}
        <span class="text-xs text-muted-foreground">No company forms loaded yet.</span>
      {/if}
    </div>
  </div>
</div>
{/if}

{#if activeFilterBadges.length > 0}
  <div class="flex flex-wrap gap-2">
    {#each activeFilterBadges as badge}
      <Badge variant="secondary" class="gap-2 pr-1">
        <span>{badge}</span>
        <button
          type="button"
          class="inline-flex h-5 w-5 items-center justify-center rounded-full cursor-pointer hover:bg-secondary-foreground/10"
          onclick={() => onFilterBadgeClear(badge)}
          aria-label={`Clear ${badge}`}
        >
          x
        </button>
      </Badge>
    {/each}
  </div>
{/if}
